import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  Compass, Target, Coins, MapPin, Calculator, Award, Clock, Users,
  Sparkles, Info, ArrowRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { Compass, Target, Coins, MapPin, Calculator, Award, Clock, Users };

// Reads the canonical Student Context (page-strip) and shows how the student's
// real situation is shaping THIS page. Deterministic; only AI part is opt-in.
export default function SituationStrip({ page = 'roadmap' }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [market, setMarket] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/context/page-strip?page=${page}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => setData({ empty: true }));
  }, [token, page]);

  if (!data || data.empty) return null;
  const { chips = [], effects = [], has_situation } = data;
  if (!has_situation && effects.length === 0) {
    return (
      <div className="rounded-2xl border border-[rgba(57,227,220,0.25)] bg-[#061736] p-4 flex items-center justify-between gap-3 flex-wrap"
        data-testid={`situation-strip-${page}`}>
        <p className="text-[13px] text-slate-300">
          Tell FuturePath your situation and this page adapts to your real life.
        </p>
        <Link to="/app/my-situation" data-testid="situation-strip-cta"
          className="text-[12px] font-semibold text-[#39E3DC] hover:underline inline-flex items-center gap-1">
          Complete My Situation <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  const askMarket = () => {
    if (market || aiBusy) return;
    setAiBusy(true);
    axios.post(`${API}/ai/market-implications`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMarket(r.data))
      .catch(() => setMarket({ implications: 'Could not generate this right now.' }))
      .finally(() => setAiBusy(false));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-[rgba(57,227,220,0.25)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 30px -14px rgba(57,227,220,0.4)' }}
      data-testid={`situation-strip-${page}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#39E3DC] animate-pulse" style={{ boxShadow: '0 0 8px #39E3DC' }} />
          <h3 className="font-outfit font-bold text-white text-base">How your situation shapes this</h3>
        </div>
        <Link to="/app/my-situation" data-testid="situation-strip-edit"
          className="text-[11px] text-[#39E3DC] hover:underline inline-flex items-center gap-1">
          Update My Situation <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="situation-strip-chips">
          {chips.map((c) => (
            <span key={c} className="text-[11px] px-2.5 py-1 rounded-full border border-[rgba(57,227,220,0.25)] bg-cyan-500/[0.06] text-slate-200">{c}</span>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {effects.map((e, i) => {
          const IconC = ICONS[e.icon] || Info;
          return (
            <div key={i} className="flex items-start gap-2.5">
              <span className="w-7 h-7 rounded-lg bg-cyan-500/[0.08] border border-[rgba(57,227,220,0.2)] flex items-center justify-center shrink-0 mt-0.5">
                <IconC className="w-3.5 h-3.5 text-[#39E3DC]" />
              </span>
              <p className="text-[12.5px] text-slate-300 leading-snug">{e.text}</p>
            </div>
          );
        })}
      </div>

      {page === 'career' && data.market?.ai && (
        <div className="mt-3 pt-3 border-t border-white/8">
          <AnimatePresence>
            {market?.implications ? (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-[12.5px] text-cyan-100 leading-relaxed" data-testid="market-implications-text">
                <Sparkles className="w-3.5 h-3.5 inline mr-1 text-[#39E3DC]" />{market.implications}
              </motion.p>
            ) : (
              <button onClick={askMarket} data-testid="market-implications-btn"
                className="text-[12px] text-[#39E3DC] hover:underline inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                {aiBusy ? 'Reading the market for you…' : 'What does the market mean for me?'}
              </button>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
}
